'use client';

import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import AuthorityBadge from '@/components/ui/AuthorityBadge';
import DemoBadge from '@/components/ui/DemoBadge';
import Link from 'next/link';
import { Building2, ShieldCheck, BookOpen, Map, Layers, GitBranch, ArrowRight, Info,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


type PF001Tab =
  | 'overview' |'architecture' |'foundation' |'codex' |'atlas' |'governance';

const tabs: { id: PF001Tab; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: Building2 },
  { id: 'architecture', label: 'Architecture', icon: GitBranch },
  { id: 'foundation', label: 'Foundation', icon: ShieldCheck },
  { id: 'codex', label: 'Codex', icon: BookOpen },
  { id: 'atlas', label: 'Atlas', icon: Map },
  { id: 'governance', label: 'Governance', icon: Layers },
];

export default function PF001Workspace() {
  const [activeTab, setActiveTab] = useState<PF001Tab>('overview');

  return (
    <div className="min-h-full max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="px-6 xl:px-8 pt-6 pb-0 border-b border-border">
        <div className="text-xs font-mono-data text-muted-foreground mb-3">
          KAVRIS → Product Framework → PF-001
        </div>
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div className="flex items-start gap-4">
            <div className="flex flex-col items-center justify-center w-14 h-14 rounded-xl border border-accent/30 bg-accent/10 flex-shrink-0">
              <span className="text-[11px] font-mono-data text-accent-foreground font-bold leading-tight">PF</span>
              <span className="text-[11px] font-mono-data text-accent-foreground font-bold leading-tight">001</span>
            </div>
            <div>
              <div className="text-xs font-mono-data text-muted-foreground mb-1 tracking-widest uppercase">
                Product Framework Item
              </div>
              <h1 className="text-2xl font-bold text-foreground leading-tight">Enterprise Edition</h1>
              <p className="text-sm text-muted-foreground mt-1">
                The first defined item within the KAVRIS Product Framework.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status="Draft" size="md" />
            <AuthorityBadge authority="Product Framework" size="md" />
            <span className="text-[11px] font-mono-data text-muted-foreground">v0.1-draft</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-0 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={`pf001-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all duration-150 whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <Icon size={13} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="p-6 xl:p-8 fade-in">
        {activeTab === 'overview' && <PF001Overview />}
        {activeTab === 'architecture' && <PF001Architecture />}
        {activeTab === 'foundation' && <PF001FoundationRelationship />}
        {activeTab === 'codex' && <PF001CodexRelationship />}
        {activeTab === 'atlas' && <PF001AtlasRelationship />}
        {activeTab === 'governance' && <PF001Governance />}
      </div>
    </div>
  );
}

function PF001Overview() {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
      <div className="xl:col-span-2 space-y-6">
        <div className="p-6 rounded-xl border border-border bg-card card-elevation">
          <div className="flex items-center gap-2 mb-4">
            <DemoBadge label="PROTOTYPE" />
            <span className="text-[10px] font-mono-data text-muted-foreground tracking-widest uppercase">Overview</span>
          </div>
          <h2 className="text-lg font-semibold text-foreground mb-3">PF-001 — Enterprise Edition</h2>
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>
              PF-001 Enterprise Edition is the first defined item within the KAVRIS Product Framework.
              It represents the enterprise-grade implementation of the KAVRIS ecosystem.
            </p>
            <p>
              PF-001 derives its structure from <strong className="text-foreground">Codex</strong>,
              its navigation from <strong className="text-foreground">Atlas</strong>, and its authority
              from <strong className="text-foreground">Foundation</strong>. It does not exist independently
              of these governing layers.
            </p>
            <p className="px-4 py-3 rounded-lg border border-border bg-muted/20 font-mono-data text-xs text-muted-foreground/80">
              Content reserved for approved KAVRIS source material. This workspace demonstrates the
              conceptual architecture of PF-001, not its final specification.
            </p>
          </div>
        </div>

        {/* Ecosystem position */}
        <div className="p-6 rounded-xl border border-border bg-card card-elevation">
          <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-4">
            Ecosystem Position
          </div>
          <div className="flex flex-col gap-2">
            {[
              { label: 'KAVRIS', level: 0, color: 'text-foreground', borderColor: 'border-foreground/20', bg: 'bg-muted/20' },
              { label: 'Foundation', level: 1, color: 'text-[#22c55e]', borderColor: 'border-[#22c55e]/25', bg: 'bg-[#22c55e]/5' },
              { label: 'Codex + Atlas', level: 1, color: 'text-primary', borderColor: 'border-primary/25', bg: 'bg-primary/5' },
              { label: 'Product Framework', level: 2, color: 'text-[#f59e0b]', borderColor: 'border-[#f59e0b]/25', bg: 'bg-[#f59e0b]/5' },
              { label: 'PF-001 Enterprise Edition', level: 3, color: 'text-accent-foreground', borderColor: 'border-accent/40', bg: 'bg-accent/8', active: true },
            ].map((item, i) => (
              <div
                key={`pos-${i}`}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border ${item.borderColor} ${item.bg} transition-all`}
                style={{ marginLeft: `${item.level * 20}px` }}
              >
                {item.level > 0 && (
                  <span className="text-muted-foreground/40 text-xs font-mono-data">↳</span>
                )}
                <span className={`text-sm font-medium ${item.color} ${item.active ? 'font-semibold' : ''}`}>
                  {item.label}
                </span>
                {item.active && (
                  <span className="ml-auto text-[10px] font-mono-data text-accent-foreground/70 bg-accent/20 px-1.5 py-0.5 rounded">
                    You are here
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right metadata */}
      <div className="space-y-5">
        <div className="p-5 rounded-xl border border-border bg-card card-elevation">
          <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-4">
            PF-001 Metadata
          </div>
          <div className="space-y-3">
            {[
              { label: 'Identifier', value: 'PF-001', mono: true },
              { label: 'Full Name', value: 'Enterprise Edition', mono: false },
              { label: 'Parent', value: 'Product Framework', mono: false },
              { label: 'Layer', value: 'Enterprise', mono: false },
              { label: 'Status', value: null, badge: 'Draft' as const },
              { label: 'Authority', value: null, authority: 'Product Framework' as const },
              { label: 'Version', value: 'v0.1-draft', mono: true },
            ].map((item, i) => (
              <div key={`meta-${i}`} className="flex items-center justify-between py-2 border-b border-border last:border-0 text-sm">
                <span className="text-muted-foreground">{item.label}</span>
                {item.value && (
                  <span className={item.mono ? 'font-mono-data text-muted-foreground text-xs' : 'text-foreground font-medium text-xs'}>
                    {item.value}
                  </span>
                )}
                {item.badge && <StatusBadge status={item.badge} size="sm" />}
                {item.authority && <AuthorityBadge authority={item.authority} size="sm" />}
              </div>
            ))}
          </div>
        </div>

        <div className="p-5 rounded-xl border border-border bg-card card-elevation">
          <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-3">
            Development Status
          </div>
          <div className="space-y-2">
            {[
              { label: 'Framework definition', done: true },
              { label: 'Foundation alignment', done: true },
              { label: 'Codex governance', done: false },
              { label: 'Atlas mapping', done: false },
              { label: 'Enterprise specification', done: false },
              { label: 'Production readiness', done: false },
            ].map((item, i) => (
              <div key={`dev-${i}`} className="flex items-center gap-2 text-xs">
                <div className={`w-3 h-3 rounded-full flex-shrink-0 border ${
                  item.done
                    ? 'bg-[#22c55e]/20 border-[#22c55e]/40'
                    : 'bg-muted/30 border-border'
                }`}>
                  {item.done && (
                    <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3">
                      <path d="M2.5 6L5 8.5L9.5 4" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <span className={item.done ? 'text-foreground' : 'text-muted-foreground'}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PF001Architecture() {
  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center gap-2 mb-2">
        <DemoBadge label="PROTOTYPE" />
      </div>

      <div className="p-6 rounded-xl border border-border bg-card card-elevation">
        <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-4">
          Architecture Concept
        </div>
        <h2 className="text-base font-semibold text-foreground mb-3">PF-001 Architectural Position</h2>
        <p className="text-sm text-muted-foreground leading-relaxed mb-4">
          PF-001 Enterprise Edition sits at the implementation layer of the KAVRIS architecture.
          It is not a standalone product — it is a governed implementation of the ecosystem.
        </p>

        {/* Architecture concept diagram */}
        <div className="relative p-6 rounded-xl bg-muted/20 border border-border overflow-hidden">
          <div className="kavris-grid-bg absolute inset-0 pointer-events-none" />
          <div className="relative space-y-3">
            {[
              {
                id: 'arch-input-foundation',
                label: 'Foundation',
                desc: 'Provides institutional authority and identity',
                side: 'left',
                color: 'border-[#22c55e]/30 bg-[#22c55e]/5 text-[#22c55e]',
              },
              {
                id: 'arch-input-codex',
                label: 'Codex',
                desc: 'Provides governance, knowledge, and principles',
                side: 'left',
                color: 'border-primary/30 bg-primary/5 text-primary',
              },
              {
                id: 'arch-input-atlas',
                label: 'Atlas',
                desc: 'Provides structural map and navigation',
                side: 'left',
                color: 'border-accent/30 bg-accent/5 text-accent-foreground',
              },
            ].map(item => (
              <div key={item.id} className={`flex items-center gap-3 p-3 rounded-lg border ${item.color}`}>
                <div className="flex-1">
                  <div className={`text-xs font-semibold ${item.color.split(' ')[2]}`}>{item.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{item.desc}</div>
                </div>
                <ArrowRight size={13} className="text-muted-foreground flex-shrink-0" />
              </div>
            ))}

            <div className="flex items-center justify-center py-2">
              <div className="flex flex-col items-center gap-1">
                <div className="w-px h-4 bg-border" />
                <div className="text-[10px] font-mono-data text-muted-foreground">converges into</div>
                <div className="w-px h-4 bg-border" />
              </div>
            </div>

            <div className="p-4 rounded-xl border border-accent/40 bg-accent/8 text-center">
              <div className="text-sm font-bold text-accent-foreground mb-1">PF-001 Enterprise Edition</div>
              <div className="text-xs text-muted-foreground">Governed enterprise implementation of KAVRIS</div>
            </div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mt-4 font-mono-data">
          Content reserved for approved KAVRIS source material. Architecture diagram is conceptual only.
        </p>
      </div>
    </div>
  );
}

function PF001FoundationRelationship() {
  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center gap-2 mb-2">
        <DemoBadge label="PROTOTYPE" />
      </div>

      <div className="p-6 rounded-xl border border-border bg-card card-elevation">
        <div className="flex items-center gap-3 mb-4">
          <ShieldCheck size={18} className="text-[#22c55e]" />
          <div>
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground">
              Foundation Relationship
            </div>
            <h2 className="text-base font-semibold text-foreground">PF-001 ↔ Executive Foundation</h2>
          </div>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed mb-6">
          PF-001 derives its institutional authority from the KAVRIS Executive Foundation.
          The Foundation documents — Constitution, Charter, Manifesto, Vision, Mission, Origin, and Product Identity —
          collectively establish the authority basis for all Product Framework implementations.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          {[
            { doc: 'Constitution', rel: 'Highest-precedence governance document' },
            { doc: 'Charter', rel: 'Operational authority and scope' },
            { doc: 'Manifesto', rel: 'Philosophical and directional framework' },
            { doc: 'Vision', rel: 'Long-term orientation of PF-001' },
            { doc: 'Mission', rel: 'Purpose alignment' },
            { doc: 'Product Identity', rel: 'Brand and identity governance' },
          ].map(item => (
            <div key={`found-rel-${item.doc}`} className="p-3 rounded-lg border border-border bg-muted/20">
              <div className="text-xs font-semibold text-[#22c55e] mb-1">{item.doc}</div>
              <div className="text-xs text-muted-foreground">{item.rel}</div>
            </div>
          ))}
        </div>

        <div className="p-3 rounded-lg bg-muted/20 border border-border text-xs text-muted-foreground font-mono-data">
          Content reserved for approved KAVRIS source material.
        </div>
      </div>
    </div>
  );
}

function PF001CodexRelationship() {
  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center gap-2 mb-2">
        <DemoBadge label="PROTOTYPE" />
      </div>

      <div className="p-6 rounded-xl border border-border bg-card card-elevation">
        <div className="flex items-center gap-3 mb-4">
          <BookOpen size={18} className="text-primary" />
          <div>
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground">
              Codex Relationship
            </div>
            <h2 className="text-base font-semibold text-foreground">PF-001 ↔ KAVRIS Codex</h2>
          </div>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed mb-5">
          The KAVRIS Codex governs PF-001 through its principles, standards, governance frameworks,
          and definitions. PF-001 must conform to all applicable Codex entries.
        </p>

        <div className="space-y-2 mb-5">
          {[
            { id: 'codex-001', title: 'Principle of Knowledge Authority', rel: 'Governs all knowledge within PF-001' },
            { id: 'codex-002', title: 'Governance Structure Definition', rel: 'Defines governance requirements for PF-001' },
            { id: 'codex-006', title: 'Product Framework Principles', rel: 'Directly governs PF-001 implementation' },
            { id: 'codex-007', title: 'Foundation Document Hierarchy', rel: 'Establishes authority basis for PF-001' },
          ].map(entry => (
            <div key={`pf001-codex-${entry.id}`} className="flex items-start gap-3 p-3 rounded-lg border border-border bg-muted/20">
              <div className="flex-1">
                <div className="text-xs font-semibold text-foreground mb-0.5">{entry.title}</div>
                <div className="text-xs text-muted-foreground">{entry.rel}</div>
              </div>
              <span className="text-[10px] font-mono-data text-muted-foreground">{entry.id}</span>
            </div>
          ))}
        </div>

        <Link
          href="/codex"
          className="group inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
        >
          Open Codex
          <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
}

function PF001AtlasRelationship() {
  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center gap-2 mb-2">
        <DemoBadge label="PROTOTYPE" />
      </div>

      <div className="p-6 rounded-xl border border-border bg-card card-elevation">
        <div className="flex items-center gap-3 mb-4">
          <Map size={18} className="text-accent-foreground" />
          <div>
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground">
              Atlas Relationship
            </div>
            <h2 className="text-base font-semibold text-foreground">PF-001 ↔ KAVRIS Atlas</h2>
          </div>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed mb-5">
          KAVRIS Atlas provides the structural map and navigational representation of PF-001
          within the broader ecosystem. PF-001 is a mapped node in the Atlas, connected to
          Foundation, Codex, Atlas itself, and the Product Framework.
        </p>

        <div className="p-4 rounded-xl border border-border bg-muted/20 mb-5">
          <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground mb-3">
            Atlas Node Data — PF-001
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            {[
              { label: 'Node ID', value: 'node-pf001' },
              { label: 'Node Type', value: 'Enterprise Edition' },
              { label: 'Layer', value: 'Enterprise' },
              { label: 'Parent Node', value: 'Product Framework' },
              { label: 'Connected Nodes', value: '4' },
              { label: 'Atlas Status', value: 'Draft' },
            ].map(item => (
              <div key={`atlas-meta-${item.label}`}>
                <div className="text-muted-foreground mb-0.5">{item.label}</div>
                <div className="font-mono-data text-foreground">{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        <Link
          href="/atlas"
          className="group inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
        >
          Open Atlas
          <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
}

function PF001Governance() {
  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center gap-2 mb-2">
        <DemoBadge label="PROTOTYPE" />
      </div>

      <div className="p-6 rounded-xl border border-border bg-card card-elevation">
        <div className="flex items-center gap-3 mb-4">
          <Layers size={18} className="text-[#f59e0b]" />
          <div>
            <div className="text-[10px] font-mono-data tracking-widest uppercase text-muted-foreground">
              Governance Context
            </div>
            <h2 className="text-base font-semibold text-foreground">PF-001 Governance</h2>
          </div>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed mb-6">
          PF-001 operates within the KAVRIS governance model. Governance is not a separate function —
          it is embedded in every layer of the architecture through Codex principles, Foundation authority,
          and Atlas structural mapping.
        </p>

        <div className="space-y-4 mb-6">
          {[
            {
              id: 'gov-authority',
              label: 'Authority Model',
              desc: 'PF-001 derives authority from Foundation → Codex → Product Framework. No element of PF-001 may contradict a higher-authority governing document.',
              status: 'Working',
            },
            {
              id: 'gov-status',
              label: 'Status Lifecycle',
              desc: 'PF-001 currently holds Draft status. Progression to Working requires alignment with all applicable Codex entries and Foundation documents.',
              status: 'Draft',
            },
            {
              id: 'gov-version',
              label: 'Version Control',
              desc: 'All changes to PF-001 must be version-tracked. Current version: v0.1-draft.',
              status: 'Working',
            },
            {
              id: 'gov-scope',
              label: 'Scope Definition',
              desc: 'Content reserved for approved KAVRIS source material.',
              status: 'Draft',
            },
          ].map(item => (
            <div key={item.id} className="p-4 rounded-xl border border-border bg-muted/20">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="text-sm font-semibold text-foreground">{item.label}</div>
                <StatusBadge status={item.status as any} size="xs" />
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="p-4 rounded-xl border border-[#a855f7]/20 bg-[#a855f7]/5">
          <div className="flex items-center gap-2 mb-2">
            <Info size={12} className="text-[#a855f7]" />
            <span className="text-[10px] font-mono-data text-[#a855f7] uppercase tracking-widest">
              Governance Note
            </span>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            This governance section is a prototype placeholder. Official governance content is reserved
            for approved KAVRIS source material and will replace these entries when defined.
          </p>
        </div>
      </div>

      {/* KAVRIS Intelligence placeholder */}
      <div className="p-5 rounded-xl border border-[#a855f7]/20 bg-[#a855f7]/5 card-elevation">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-[#a855f7] animate-pulse" />
          <div className="text-[10px] font-mono-data tracking-widest uppercase text-[#a855f7]">
            KAVRIS Intelligence
          </div>
          <span className="ml-auto text-[9px] font-mono-data text-muted-foreground bg-muted/30 px-1.5 py-0.5 rounded">
            Prototype interaction
          </span>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
          In a production system, this layer would provide contextual intelligence — surfacing governing
          principles, related architecture, and navigational recommendations specific to PF-001 governance.
        </p>
        <div className="space-y-1.5">
          {[
            'Explain this governance relationship.',
            'Navigate to the governing principle.',
            'Show related architecture.',
            'Find related knowledge.',
          ].map((q, qi) => (
            <div
              key={`gov-q-${qi}`}
              className="text-xs text-muted-foreground/70 px-2 py-1.5 rounded border border-border bg-muted/20 font-mono-data"
            >
              {q}
            </div>
          ))}
        </div>
        <p className="text-[10px] font-mono-data text-muted-foreground/50 mt-3">
          Mock data only — no external AI API connected
        </p>
      </div>
    </div>
  );
}